/**
 * Authentication layout component
 * Provides consistent layout for login, register, and other auth pages
 * Matches project architecture patterns with proper exports and component structure
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Container, AppBar, Toolbar } from '@mui/material';
import { Typography } from '@components/wrappers';
import { FontFamily } from '@styles/index';

/**
 * Authentication layout component
 * @returns JSX Element
 */
export const AuthLayout: React.FC = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflowX: 'hidden',
        bgcolor: 'background.primary.dark',
      }}
    >
      {/* AppBar for Auth Pages */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#274375' }}>
        <Container maxWidth="xl">
          <Toolbar />
        </Container>
      </AppBar>

     

      {/* Logo Section */}
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          component="img"
          src="/assets/who_logo.svg"
          alt="World Health Organization Logo"
          sx={{
            height: { xs: 60, sm: 68, md: 74 },
            width: 'auto',
            maxWidth: { xs: 180, sm: 195, md: 205 },
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flexGrow: 1,
          m: { xs: 1, md: 1 },
          gap: { xs: 4, md: 2 },
        }}
      >
        {/* Welcome Text Section */}
        <Box
          sx={{
            ml: { xs: 2, md: '32px' },
            pt: { xs: 2, md: '40px' },
            width: { xs: '100%', md: '50%' },
            flexBasis: { xs: '100%', md: '60%' },
            flexGrow: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              fontFamily: FontFamily.primary,
              color: '#0f1d2c',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            Welcome to the AI Marketplace
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: FontFamily.primary,
              fontSize: { xs: '14px', sm: '15px', md: '16px' },
              mt: { xs: 2, md: '24px' },
              color: '#0f1d2c',
              lineHeight: 1.6,
            }}
          >
          A centralised platform that brings together a wide range of artificial intelligence tools in one easy-to-use dashboard. 
          Users can access writing, design, automation, chatbots, marketing, productivity, and learning tools from a single location.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: FontFamily.primary,
              mt: { xs: 1.5, md: '18px' },
              fontSize: { xs: '14px', sm: '15px', md: '16px' },
              color: '#0f1d2c',
              lineHeight: 1.6,
            }}
          >
            Log in to begin your journey.
          </Typography>
        </Box>

        {/* Auth Form Section */}
        <Box
          component="main"
          sx={{
            flexGrow: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 2, md: 4 },
            width: { xs: '100%', md: 'auto' },
            flexBasis: { xs: '100%', md: '40%' },
            px: { xs: 2, md: 0 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
